import React, { createContext, useContext, useState } from "react";
import { useWorkflowContext } from "../../Workflow/WorkflowContextProvider";

// Create the Form Interaction Context
const FormInteractionContext = createContext();

/**
 * FormInteractionProvider component that manages form interaction state (selectedElement, isSaving)
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const FormInteractionProvider = ({ children }) => {
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const { setSelectedVariable } = useWorkflowContext();
  const [isSaving, setIsSaving] = useState(false);

  // Helper functions to handle mutual exclusivity
  const selectElement = (element) => {
    setSelectedElement(element);
    setSelectedSubsection(null); // Deselect subsection when selecting element
  };

  const selectSubsection = (subsection) => {
    setSelectedSubsection(subsection);
    setSelectedElement(null); // Deselect element when selecting subsection
    setSelectedVariable(null);
  };

  const clearSelection = () => {
    setSelectedElement(null);
    setSelectedSubsection(null);
  };

  // Context value object
  const value = {
    selectedElement,
    selectedSubsection,
    setSelectedElement: selectElement,
    setSelectedSubsection: selectSubsection,
    clearSelection,
    isSaving,
    setIsSaving,
  };

  return <FormInteractionContext.Provider value={value}>{children}</FormInteractionContext.Provider>;
};

/**
 * Custom hook to use the Form Interaction Context
 * @returns {Object} Form interaction context value
 * @throws {Error} If used outside of FormInteractionProvider
 */
export const useFormInteraction = () => {
  const context = useContext(FormInteractionContext);

  if (context === undefined) {
    throw new Error("useFormInteraction must be used within a FormInteractionProvider");
  }

  return context;
};

export default FormInteractionContext;
